import { apiFetch } from '~/utils/apiFetch';
import { setCookie } from '~/utils/cookies.client';
import { enrichOrderItems } from '~/utils/enrichOrderItem';

export async function pickupCartClient(): Promise<string> {
  const API_URL = window?.ENV?.API_URL || '';

  const response = await apiFetch(`${API_URL}/api/v2/shop/orders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });

  if (!response.ok) throw new Error('Failed to create a new cart');

  const data = await response.json();

  setCookie('orderToken', data.tokenValue, { maxAge: 2592000 });

  return data.tokenValue;
}

export async function fetchOrderFromAPIClient(token: string, throwOnFail = false, withDetails = true) {
  const API_URL = window?.ENV?.API_URL || '';

  const response = await apiFetch(`${API_URL}/api/v2/shop/orders/${token}`);
  if (!response.ok) {
    if (throwOnFail) throw new Error('Failed to fetch order');
    return null;
  }

  const order = await response.json();

  // Enrich items with product details (images, stock, etc.)
  if (withDetails && order.items?.length > 0) {
    order.items = await enrichOrderItems(order.items, API_URL);
  }

  return order;
}

export async function updateOrderItemAPIClient({
  id,
  quantity,
  token,
}: {
  id: number;
  quantity: number;
  token: string;
}) {
  const API_URL = window?.ENV?.API_URL || '';

  const response = await apiFetch(`${API_URL}/api/v2/shop/orders/${token}/items/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/merge-patch+json' },
    body: JSON.stringify({ quantity }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const message = data?.['hydra:description'] || data?.detail || 'Failed to update item';
    console.error('updateOrderItemAPIClient failed:', response.status, message);
    throw new Error(message);
  }

  return await response.json();
}

export async function removeOrderItemAPIClient({ id, token }: { id: number; token: string }) {
  const API_URL = window?.ENV?.API_URL || '';

  const response = await apiFetch(`${API_URL}/api/v2/shop/orders/${token}/items/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) throw new Error('Failed to remove item');
}

export async function setOrderAddressEmailAndAddressesAPIClient(params: {
  token: string;
  email: string;
  billingAddress: object;
  shippingAddress: object;
}) {
  const { token, email, billingAddress, shippingAddress } = params;
  const API_URL = window?.ENV?.API_URL || '';

  const res = await apiFetch(`${API_URL}/api/v2/shop/orders/${token}/address`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/merge-patch+json' },
    body: JSON.stringify({
      email,
      billingAddress,
      shippingAddress,
    }),
  });

  if (!res.ok) throw new Error('Failed to set address and email');

  return await res.json();
}

export async function applyCouponCodeClient(token: string, code: string): Promise<void> {
  const API_URL = window?.ENV?.API_URL || '';

  const res = await apiFetch(`${API_URL}/api/v2/shop/orders/${token}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/ld+json' },
    body: JSON.stringify({ couponCode: code }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.['hydra:description'] || data?.detail || 'Failed to apply coupon code');
  }
}

export async function removeCouponCodeClient(token: string): Promise<void> {
  const API_URL = window?.ENV?.API_URL || '';

  const res = await apiFetch(`${API_URL}/api/v2/shop/orders/${token}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/ld+json' },
    body: JSON.stringify({ couponCode: null }),
  });

  if (!res.ok) throw new Error('Failed to remove coupon code');
}
